import React, { useState, useRef } from "react";
import "./modal_data_transfer_out.css";
import { Table, Input, Button, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined } from "@ant-design/icons";
import type { InputRef } from 'antd';
import type { FilterConfirmProps, FilterDropdownProps } from 'antd/es/table/interface';
import useSWR from "swr";
import axios from "axios";

interface ModalDataTransferOutProps {
    isOpen: boolean;
    onRowDoubleClick: (record: DataType) => void;
}

interface DataType {
    key: React.Key;
    transferOutNo: string;
    date: string;
    requestNo: string;
    requestBy: string;
    fromWarehouse: string;
    fromWarehouseCode: string;
}

const fetcher = (url: string) => axios.get(url).then((res) => res.data);

const ModalDataTransferOut: React.FC<ModalDataTransferOutProps> = ({ isOpen, onRowDoubleClick }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const { data, error } = useSWR(
        isOpen ? `${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-out-data` : null,
        fetcher
    );

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: keyof DataType,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: keyof DataType): object => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value: any, record: DataType) => 
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes(String(value).toLowerCase()), 
        onFilterDropdownOpenChange: (visible: boolean) => { 
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
    });

    const parseDate = (dateStr: string) => {
        const [day, month, year] = dateStr.split('/');
        return new Date(`${year}-${month}-${day}`).getTime();
    };

    const columns: ColumnsType<DataType> = [
        {
            title: "Transfer Out No",
            dataIndex: "transferOutNo",
            key: "transferOutNo",
            sorter: (a, b) => a.transferOutNo.localeCompare(b.transferOutNo),
            ...getColumnSearchProps('transferOutNo'),
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            sorter: (a, b) => parseDate(a.date) - parseDate(b.date),
            ...getColumnSearchProps('date'),
        },
        {
            title: "Request No",
            dataIndex: "requestNo",
            key: "requestNo",
            sorter: (a, b) => a.requestNo.localeCompare(b.requestNo),
            ...getColumnSearchProps('requestNo'),
        },
        {
            title: "Request By",
            dataIndex: "requestBy",
            key: "requestBy",
            sorter: (a, b) => a.requestBy.localeCompare(b.requestBy),
            ...getColumnSearchProps('requestBy'),
        },
        {
            title: "From Warehouse",
            dataIndex: "fromWarehouse",
            key: "fromWarehouse",
            sorter: (a, b) => a.fromWarehouse.localeCompare(b.fromWarehouse),
            ...getColumnSearchProps('fromWarehouse'),
        },
        {
            title: "From Warehouse Code",
            dataIndex: "fromWarehouseCode",
            key: "fromWarehouseCode",
            sorter: (a, b) => a.fromWarehouseCode.localeCompare(b.fromWarehouseCode),
            ...getColumnSearchProps('fromWarehouseCode'),
        },
    ];

    if (error) return <div>Failed to load data.</div>;

    const tableData: DataType[] = data?.map((item: any) => ({
        key: item.id,
        transferOutNo: item.trans_out_no,
        date: item.trans_out_date,
        requestNo: item.trans_request_no,
        requestBy: item.request_by,
        fromWarehouse: item.warehouse_name_req,
        fromWarehouseCode: item.warehouse_code_req,
    })) || [];

    return (
        <div id="iiok" style={transformStyle}>
            <div className="gjs-grid-row" id="i9ul">
                { }
                <div className="gjs-grid-column" id="ise1">
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        loading={!data && !error}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    onRowDoubleClick(record);
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModalDataTransferOut;